'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import {
  Landmark, Scale, IndianRupee, Megaphone, Store, Briefcase,
  PenLine, Moon, CalendarDays, Trophy, KeyRound, Info, Phone, Sparkles, BookOpen,
  ShieldCheck, ChevronDown, LayoutDashboard, Tags, LogOut, UserRound, MapPin, Bell,
  Menu, X, Users,
} from 'lucide-react'

/* ─────────────────────────────────────────────────────────────
   Homepage navbar — Fable chrome with the approved grouped menu.
   Dark-glass over the hero, cream + white panels once scrolled.
───────────────────────────────────────────────────────────────*/

/* `flag` (optional) ties a nav item to a Module Control feature key. When the
   admin switches that module OFF the item is hidden here AND its route is 404'd
   by middleware. Items with no flag (About, Contact, Claim, MasjidOS) always
   show. A group whose items all hide is dropped entirely. */
interface Item { label: string; href: string; desc: string; Icon: any; flag?: string }
interface Group { label: string; items: Item[]; href?: string; flag?: string }

const GROUPS: Group[] = [
  {
    label: 'Waqf', items: [
      { label: 'Waqf Properties', href: '/waqf/listings', desc: 'Browse registered waqf properties across India', Icon: Landmark, flag: 'waqfGuardian' },
      { label: 'Asset Management & Protection', href: '/waqf', desc: 'Evidence-first waqf protection system', Icon: Scale, flag: 'waqfGuardian' },
    ],
  },
  // Ummah is a DIRECT top-level link to the national community feed (one destination).
  { label: 'Ummah', href: '/feed', flag: 'feed', items: [] },
  // Give is a DIRECT link straight to campaigns — no submenu (one destination).
  { label: 'Give', href: '/give?tab=campaigns', flag: 'campaigns', items: [] },
  {
    label: 'Connect', items: [
      { label: 'Announcements', href: '/announcements', desc: 'Masjid notices and event updates', Icon: Megaphone, flag: 'announcements' },
      { label: 'Contact', href: '/contact', desc: 'Get in touch', Icon: Phone },
    ],
  },
  {
    label: 'Services', items: [
      { label: 'Community+', href: '/business', desc: 'Halal businesses near you', Icon: Store, flag: 'communityPlus' },
      { label: 'Masjid Connect', href: '/connect', desc: 'Find a skilled neighbour who can help', Icon: Users, flag: 'masjidConnect' },
      { label: 'Job Portal', href: '/jobs', desc: 'Islamic job listings', Icon: Briefcase, flag: 'jobsPortal' },
      { label: 'MasjidOS', href: '/dashboard', desc: 'Committee operating system', Icon: LayoutDashboard },
      { label: 'Ramadan', href: '/ramadan', desc: 'Sehri/Iftar, 30-day planner, daily duas', Icon: Moon, flag: 'ramadanModeLive' },
      { label: 'Islamic Calendar', href: '/islamic-calendar', desc: 'Key Islamic dates', Icon: CalendarDays, flag: 'islamicCalendar' },
      { label: 'Claim Masjid', href: '/masjid/claim', desc: 'Take control of your masjid', Icon: KeyRound, flag: 'addListMasjid' },
    ],
  },
  {
    label: 'Explore', items: [
      { label: 'Volunteers', href: '/volunteers', desc: 'Help map every masjid in India', Icon: Sparkles, flag: 'volunteerNetwork' },
      { label: 'Blog', href: '/blog', desc: 'Articles from scholars', Icon: PenLine, flag: 'blog' },
      { label: 'Pricing', href: '/pricing', desc: 'Plans for every masjid', Icon: Tags, flag: 'pricingPage' },
      { label: 'About Us', href: '/about', desc: 'Our mission and team', Icon: Info },
    ],
  },
]

const ROLE_DASHBOARD: Record<string, string> = {
  admin: '/admin',
  'masjid-admin': '/dashboard',
  masjid_admin: '/dashboard',
  volunteer: '/volunteer/dashboard',
  moderator: '/moderator/dashboard',
}

export default function HomeNav({ initialFlags = {} }: { initialFlags?: Record<string, boolean> }) {
  /* SSR-correct flag gating: visibility comes from the SERVER-resolved flag map threaded from the
     root layout (getServerFeatureFlags), NOT the client hook that started all-ON and flipped
     post-mount. So an OFF module's nav link is ABSENT from the initial HTML on every route (no
     flash, crawler-correct). `!== false` preserves fail-open — a key missing from the map shows. */
  const on = (k?: string) => !k || initialFlags[k] !== false
  // Drop module items the admin switched off, then drop groups left empty.
  const groups = GROUPS
    .map((g) => ({ ...g, items: g.items.filter((it) => on(it.flag)) }))
    // direct-link groups (href) survive on their own flag; dropdowns only if non-empty
    .filter((g) => (g.href ? on(g.flag) : g.items.length > 0))
  const showMasjids = on('masjidDirectory')
  const [scrolled, setScrolled] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [profileOpen, setProfileOpen] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [user, setUser] = useState<any>(null)
  const [adminAuth, setAdminAuth] = useState(false)
  const rootRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    fetch('/api/auth/me').then((r) => r.json()).then((d) => { if (d?.user) setUser(d.user) }).catch(() => {})
    try { setAdminAuth(localStorage.getItem('admin_auth') === 'true') } catch {}
  }, [])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) { setOpen(null); setProfileOpen(false) }
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setOpen(null); setProfileOpen(false); setMobileOpen(false) } }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('keydown', onKey) }
  }, [])

  const isAdmin = adminAuth || user?.role === 'admin'
  const dashboardHref = ROLE_DASHBOARD[user?.role] || '/account'
  const initial = (user?.name || 'U').charAt(0).toUpperCase()

  const logout = () => {
    fetch('/api/auth/logout', { method: 'POST' }).catch(() => {})
    try { localStorage.removeItem('admin_auth') } catch {}
    window.location.href = '/login'
  }

  /* Admin signs in via the env-var admin login (admin_session cookie + the
     admin_auth localStorage flag) — NOT a DB user. Without this, the navbar
     showed BOTH the Admin pill and a "Sign in" button (it only knew about
     `user`). Clear the admin session explicitly. */
  const adminLogout = () => {
    fetch('/api/admin/login', { method: 'DELETE' }).catch(() => {})
    try { localStorage.removeItem('admin_auth') } catch {}
    window.location.href = '/'
  }

  return (
    <nav ref={rootRef} className={scrolled ? 'scrolled' : ''}>
      <div className="container nav-inner">
        <Link className="brand" href="/" aria-label="Masjids.in home">
          <Image className="lg-light" src="/homepage-assets/brand-logo-footer.png" alt="Masjids.in" width={244} height={70} priority />
          <Image className="lg-dark" src="/homepage-assets/brand-logo.png" alt="Masjids.in" width={244} height={70} priority />
        </Link>

        <div className="nav-links">
          {showMasjids && <Link href="/masjids" className="nav-link">Masjids</Link>}
          {groups.map((g) => (g.href ? (
            <Link key={g.label} href={g.href} className="nav-link">{g.label}</Link>
          ) : (
            <div className="dd" key={g.label}>
              <button
                className={'dd-btn' + (open === g.label ? ' on' : '')}
                onClick={() => setOpen(open === g.label ? null : g.label)}
                aria-expanded={open === g.label}
              >
                {g.label} <ChevronDown size={13} strokeWidth={2} style={{ transform: open === g.label ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }} />
              </button>
              {open === g.label && (
                <div className={'dd-panel' + (g.items.length >= 5 ? ' two' : '')}>
                  {g.items.map((it) => (
                    <Link key={it.href + it.label} href={it.href} className="dd-item" onClick={() => setOpen(null)}>
                      <span className="dd-ic"><it.Icon size={16} strokeWidth={1.5} /></span>
                      <span className="dd-tx"><b>{it.label}</b><small>{it.desc}</small></span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )))}
        </div>

        <div className="nav-spacer" />

        {showMasjids && <Link href="/directory?city=Hyderabad" className="nav-city"><MapPin size={12} strokeWidth={1.5} /> Hyderabad</Link>}
        {isAdmin && <Link href="/admin" className="nav-admin"><ShieldCheck size={13} strokeWidth={1.5} /> Admin</Link>}
        {user && on('notifications') && <Link href="/notifications" className="nav-bell" aria-label="Notifications"><Bell size={15} strokeWidth={1.5} /></Link>}
        {/* Web whole-page language toggle REMOVED 2026-10-24 — it drove Google Translate, which no
            longer translates on the deployed domain. First-party web i18n is planned. */}
        {on('addListMasjid') && <Link href="/onboard" className="btn-green">Register Masjid</Link>}

        {user ? (
          <div className="dd profile">
            <button className="avatar" onClick={() => setProfileOpen(!profileOpen)} aria-expanded={profileOpen}>{initial}</button>
            {profileOpen && (
              <div className="dd-panel right">
                <Link href={dashboardHref} className="dd-item" onClick={() => setProfileOpen(false)}>
                  <span className="dd-ic"><LayoutDashboard size={16} strokeWidth={1.5} /></span>
                  <span className="dd-tx"><b>Dashboard</b><small>{user.name}</small></span>
                </Link>
                <Link href="/account" className="dd-item" onClick={() => setProfileOpen(false)}>
                  <span className="dd-ic"><UserRound size={16} strokeWidth={1.5} /></span>
                  <span className="dd-tx"><b>My Account</b><small>Profile &amp; preferences</small></span>
                </Link>
                <button className="dd-item as-btn" onClick={logout}>
                  <span className="dd-ic"><LogOut size={16} strokeWidth={1.5} /></span>
                  <span className="dd-tx"><b>Sign Out</b><small>End this session</small></span>
                </button>
              </div>
            )}
          </div>
        ) : isAdmin ? (
          <button className="btn-ghost as-btn" onClick={adminLogout} aria-label="Sign out of admin" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <LogOut size={15} strokeWidth={1.8} /> Sign out
          </button>
        ) : (
          <Link href="/login" className="btn-ghost">Sign in</Link>
        )}

        {/* Mobile-only hamburger (≤980px) — opens the drawer holding the items
            hidden at this width: nav links, city selector, language switcher. */}
        <button
          className="nav-burger"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={mobileOpen}
          aria-controls="nav-mobile-drawer"
        >
          {mobileOpen ? <X size={20} strokeWidth={2} /> : <Menu size={20} strokeWidth={2} />}
        </button>
      </div>

      {mobileOpen && (
        <div className="nav-drawer-backdrop" onClick={() => setMobileOpen(false)}>
          <div
            id="nav-mobile-drawer"
            className="nav-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Primary CTAs live here on mobile (≤980px) — moved out of the top
                bar so it stays LOGO + BURGER only and never overflows 390px. */}
            <div className="md-cta">
              {on('addListMasjid') && <Link href="/onboard" className="md-btn green" onClick={() => setMobileOpen(false)}>Register Masjid</Link>}
              {user ? (
                <>
                  <Link href={dashboardHref} className="md-btn" onClick={() => setMobileOpen(false)}>Dashboard</Link>
                  <Link href="/account" className="md-btn" onClick={() => setMobileOpen(false)}>My Account</Link>
                  <button className="md-btn as-btn" onClick={logout}>Sign Out</button>
                </>
              ) : isAdmin ? (
                <button className="md-btn as-btn" onClick={adminLogout}>Sign out of admin</button>
              ) : (
                <Link href="/login" className="md-btn" onClick={() => setMobileOpen(false)}>Sign in</Link>
              )}
              {isAdmin && <Link href="/admin" className="md-btn" onClick={() => setMobileOpen(false)}>Admin</Link>}
            </div>
            {showMasjids && <Link href="/masjids" className="md-link" onClick={() => setMobileOpen(false)}>Masjids</Link>}
            {groups.map((g) => (g.href ? (
              <Link key={g.label} href={g.href} className="md-link" onClick={() => setMobileOpen(false)}>{g.label}</Link>
            ) : (
              <div className="md-group" key={g.label}>
                <div className="md-group-label">{g.label}</div>
                {g.items.map((it) => (
                  <Link key={it.href + it.label} href={it.href} className="md-item" onClick={() => setMobileOpen(false)}>
                    <span className="md-ic"><it.Icon size={16} strokeWidth={1.5} /></span>
                    <span className="md-tx"><b>{it.label}</b><small>{it.desc}</small></span>
                  </Link>
                ))}
              </div>
            )))}
            <div className="md-controls">
              {showMasjids && <Link href="/directory?city=Hyderabad" className="md-link" onClick={() => setMobileOpen(false)}><MapPin size={13} strokeWidth={1.5} /> Hyderabad</Link>}
              {/* Language toggle removed 2026-10-24 (Google Translate retired on web). */}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        nav{position:sticky;top:0;z-index:50;background:#0B132B;border-bottom:1px solid rgba(255,255,255,.06);transition:background .3s,border-color .3s,box-shadow .3s}
        nav.scrolled{backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);background:rgba(251,250,246,.88);border-bottom:1px solid rgba(11,19,43,.08);box-shadow:0 8px 30px rgba(11,19,43,.08)}
        .container{max-width:1280px;margin:0 auto;padding:0 26px}
        .nav-inner{display:flex;align-items:center;gap:14px;height:64px}
        nav :global(.brand){display:block;width:132px;height:38px;flex:none}
        nav :global(.brand img){height:38px;width:132px;object-fit:contain;object-position:left center;display:block}
        nav :global(.brand img.lg-dark){display:none}
        nav.scrolled :global(.brand img.lg-light){display:none}
        nav.scrolled :global(.brand img.lg-dark){display:block}

        .nav-links{display:flex;align-items:center;gap:4px;margin-left:8px}
        nav :global(.nav-link){color:rgba(246,244,238,.78);text-decoration:none;font-size:13.5px;font-weight:500;padding:7px 11px;border-radius:9px;transition:color .2s,background .2s}
        nav :global(.nav-link:hover){color:#14B8A6}
        nav.scrolled :global(.nav-link){color:#5E6A85}
        nav.scrolled :global(.nav-link:hover){color:#0E7C61;background:rgba(14,124,97,.06)}

        .dd{position:relative}
        .dd-btn{display:inline-flex;align-items:center;gap:4px;background:none;border:none;cursor:pointer;font-family:inherit;font-size:13.5px;font-weight:500;color:rgba(246,244,238,.78);padding:7px 11px;border-radius:9px;transition:color .2s,background .2s;white-space:nowrap}
        .dd-btn:hover,.dd-btn.on{color:#14B8A6}
        nav.scrolled .dd-btn{color:#5E6A85}
        nav.scrolled .dd-btn:hover,nav.scrolled .dd-btn.on{color:#0E7C61;background:rgba(14,124,97,.06)}

        /* dropdown panels — dark glass over hero, white when scrolled */
        .dd-panel{position:absolute;top:calc(100% + 8px);left:0;z-index:300;min-width:268px;padding:7px;border-radius:14px;
          background:rgba(13,21,44,.94);border:1px solid rgba(255,255,255,.12);
          backdrop-filter:blur(18px);-webkit-backdrop-filter:blur(18px);
          box-shadow:0 22px 60px rgba(4,8,20,.55)}
        .dd-panel.two{min-width:520px;display:grid;grid-template-columns:1fr 1fr;gap:2px}
        .dd-panel.right{left:auto;right:0}
        nav.scrolled .dd-panel{background:#fff;border-color:rgba(11,19,43,.1);box-shadow:0 16px 48px rgba(11,19,43,.16)}
        nav :global(.dd-item){display:flex;gap:11px;align-items:flex-start;padding:9px 11px;border-radius:10px;text-decoration:none;transition:background .15s;color:inherit}
        nav :global(.dd-item:hover){background:rgba(20,184,166,.1)}
        nav.scrolled :global(.dd-item:hover){background:rgba(14,124,97,.07)}
        .as-btn{width:100%;background:none;border:none;cursor:pointer;font-family:inherit;text-align:left}
        nav :global(.dd-ic){width:32px;height:32px;border-radius:9px;display:grid;place-items:center;flex:none;background:rgba(20,184,166,.14);color:#14B8A6}
        nav.scrolled :global(.dd-ic){background:rgba(14,124,97,.08);color:#0E7C61}
        nav :global(.dd-tx b){display:block;font-size:12.5px;font-weight:600;color:#F6F4EE;line-height:1.3}
        nav :global(.dd-tx small){display:block;font-size:10.5px;color:rgba(246,244,238,.55);line-height:1.35;margin-top:1px}
        nav.scrolled :global(.dd-tx b){color:#0B132B}
        nav.scrolled :global(.dd-tx small){color:#5E6A85}

        .nav-spacer{flex:1}
        nav :global(.nav-city){display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:500;color:rgba(246,244,238,.78);text-decoration:none;border:1px solid rgba(255,255,255,.18);border-radius:9px;padding:6px 11px;background:rgba(255,255,255,.06);white-space:nowrap}
        nav.scrolled :global(.nav-city){color:#5E6A85;border-color:rgba(11,19,43,.14);background:#fff}
        nav :global(.nav-bell){display:inline-flex;align-items:center;justify-content:center;width:34px;height:34px;color:rgba(246,244,238,.78);text-decoration:none;border:1px solid rgba(255,255,255,.18);border-radius:9px;background:rgba(255,255,255,.06)}
        nav.scrolled :global(.nav-bell){color:#5E6A85;border-color:rgba(11,19,43,.14);background:#fff}
        nav :global(.nav-admin){display:inline-flex;align-items:center;gap:5px;font-size:12px;font-weight:600;color:#E6C963;text-decoration:none;border:1px solid rgba(212,175,55,.4);border-radius:9px;padding:6px 11px;background:rgba(212,175,55,.08)}
        nav.scrolled :global(.nav-admin){color:#7c3aed;border-color:rgba(124,58,237,.3);background:rgba(124,58,237,.06)}
        .nav-lang :global(div > button:first-child){background:rgba(255,255,255,.08)!important;border-color:rgba(255,255,255,.18)!important;color:#F6F4EE!important}
        nav.scrolled .nav-lang :global(div > button:first-child){background:#f5f5f3!important;border-color:#e0e0da!important;color:#444!important}
        nav :global(.btn-green){background:linear-gradient(135deg,#0E7C61,#0A5C49);color:#fff;font-weight:600;font-size:13px;border:none;border-radius:10px;padding:9px 17px;cursor:pointer;box-shadow:0 2px 14px rgba(14,124,97,.3);transition:transform .15s;text-decoration:none;display:inline-block;white-space:nowrap}
        nav :global(.btn-green:hover){transform:translateY(-1px)}
        nav :global(.btn-ghost){background:transparent;border:1px solid rgba(255,255,255,.28);color:#F6F4EE;border-radius:10px;padding:8px 14px;font-size:13px;font-weight:500;cursor:pointer;text-decoration:none;transition:all .3s;white-space:nowrap}
        nav.scrolled :global(.btn-ghost){background:#fff;border-color:rgba(11,19,43,.14);color:#15203C}
        .avatar{width:36px;height:36px;border-radius:50%;border:2px solid rgba(255,255,255,.2);background:linear-gradient(135deg,#0E7C61,#14B8A6);color:#fff;font-weight:700;font-size:14px;cursor:pointer;display:grid;place-items:center;font-family:inherit}
        nav.scrolled .avatar{border-color:rgba(11,19,43,.1)}

        @media(max-width:1180px){
          nav :global(.dd-tx small){display:none}
          .dd-panel{min-width:220px}
          .dd-panel.two{min-width:420px}
        }
        /* mobile hamburger + drawer (FIX 1) — hidden on desktop */
        .nav-burger{display:none;align-items:center;justify-content:center;width:40px;height:40px;border:none;background:transparent;color:#F6F4EE;cursor:pointer;border-radius:9px;flex:none;margin-left:4px}
        .nav-burger:hover{background:rgba(255,255,255,.08)}
        nav.scrolled .nav-burger{color:#0B132B}
        nav.scrolled .nav-burger:hover{background:rgba(11,19,43,.06)}
        .nav-drawer-backdrop{position:fixed;inset:0;z-index:60;background:rgba(7,13,28,.55);-webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);display:flex;justify-content:flex-end}
        .nav-drawer{width:min(320px,86vw);height:100vh;overflow-y:auto;background:#0B132B;border-left:1px solid rgba(255,255,255,.08);padding:18px 14px 28px;box-shadow:-16px 0 48px rgba(0,0,0,.4);display:flex;flex-direction:column;gap:4px;-webkit-overflow-scrolling:touch}
        .nav-drawer :global(.md-link){display:flex;align-items:center;gap:8px;padding:11px 12px;border-radius:10px;font-size:14px;font-weight:600;color:#F6F4EE;text-decoration:none}
        .nav-drawer :global(.md-link:hover){background:rgba(255,255,255,.06)}
        .md-group{margin-top:6px;display:flex;flex-direction:column;gap:2px}
        .md-group-label{font-family:var(--f-mono,monospace);font-size:10px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:rgba(246,244,238,.45);padding:8px 12px 4px}
        .nav-drawer :global(.md-item){display:flex;align-items:flex-start;gap:10px;padding:9px 12px;border-radius:10px;text-decoration:none}
        .nav-drawer :global(.md-item:hover){background:rgba(255,255,255,.06)}
        .nav-drawer :global(.md-ic){width:30px;height:30px;border-radius:8px;background:rgba(255,255,255,.06);color:#7DEFE0;display:grid;place-items:center;flex:none}
        .nav-drawer :global(.md-tx b){display:block;font-size:13px;font-weight:600;color:#F6F4EE;line-height:1.3}
        .nav-drawer :global(.md-tx small){display:block;font-size:11px;color:rgba(246,244,238,.55);line-height:1.35;margin-top:1px}
        .md-controls{margin-top:14px;padding-top:14px;border-top:1px solid rgba(255,255,255,.08);display:flex;flex-direction:column;gap:10px}
        /* Drawer CTAs (mobile auth row relocated from the top bar) */
        .md-cta{display:flex;flex-direction:column;gap:8px;padding-bottom:12px;margin-bottom:6px;border-bottom:1px solid rgba(255,255,255,.08)}
        .nav-drawer :global(.md-btn){display:block;width:100%;text-align:center;padding:11px 14px;border-radius:10px;font-size:13.5px;font-weight:600;text-decoration:none;cursor:pointer;font-family:inherit;border:1px solid rgba(255,255,255,.22);background:rgba(255,255,255,.06);color:#F6F4EE;transition:background .2s,border-color .2s}
        .nav-drawer :global(.md-btn:hover){background:rgba(255,255,255,.12)}
        .nav-drawer :global(.md-btn.green){background:linear-gradient(135deg,#0E7C61,#0A5C49);border-color:transparent;color:#fff;box-shadow:0 2px 14px rgba(14,124,97,.3)}

        @media(max-width:980px){
          .nav-links{display:none}
          .nav-lang{display:none}
          nav :global(.nav-city){display:none}
          .nav-burger{display:inline-flex}
          /* Top bar = LOGO + BURGER only on mobile. Every auth/CTA control moves
             into the drawer (.md-cta) so the row fits ~390px without overflow. */
          nav :global(.btn-green), nav :global(.btn-ghost), nav :global(.nav-bell), nav :global(.nav-admin){display:none}
          .profile{display:none}
        }
        @media(min-width:981px){
          .nav-drawer-backdrop{display:none}
        }
      `}</style>
    </nav>
  )
}
